// CountrySelect.jsx
// Searchable country dropdowns for the client form.
//   • SearchableCountry — a single inline-expanding, searchable country picker.
//   • CountryFields      — the three LINKED fields (Nationality, Country of
//                          Residence, Phone code) used in Step1. Picking a
//                          country in any one pre-populates the others *when
//                          they are still empty*, so an independent choice
//                          (e.g. Nigerian living in the UK) is never clobbered.
//
// Stored values keep the SAME field names/shapes the backend already expects:
//   nationality        -> demonym string (e.g. "Nigerian")
//   countryOfResidence -> country name   (e.g. "Nigeria")
//   phone              -> "<dial> <number>" string (e.g. "+234 8012345678")
//
// The dropdown expands inline (it doesn't use absolute positioning) so it is
// never clipped by the wizard's overflow-y-auto scroll container.
import React, { useState, useRef, useEffect } from "react";
import { Ico } from "./primitives";
import { COUNTRIES, findCountry, splitPhone } from "./countries";
import { G } from "./constants";

// Label markup kept consistent with the other field labels in this module.
const FieldLabel = ({ children }) => (
  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
    {children}
  </label>
);

const SearchIcon = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="2"
    className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
  >
    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// ─── A single searchable country picker ──────────────────────────────────────
// mode "name" → trigger shows the country name; mode "dial" → shows the dial code.
export function SearchableCountry({
  label,
  selected,
  onSelect,
  mode = "name",
  placeholder = "Select country",
  className = "",
  triggerClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  // Close when clicking outside this field.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const list = !q
    ? COUNTRIES
    : COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.demonym.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.code.toLowerCase() === q
      );

  const pick = (c) => {
    onSelect(c);
    setOpen(false);
    setQuery("");
  };

  const triggerText = selected
    ? mode === "dial"
      ? `${selected.flag} ${selected.dial}`
      : `${selected.flag} ${selected.name}`
    : placeholder;

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && <FieldLabel>{label}</FieldLabel>}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl border text-sm bg-white text-left transition-colors ${
          open ? "border-[rgb(var(--brand-rgb))] ring-2 ring-[rgb(var(--brand-rgb)/0.2)]" : "border-slate-200 hover:border-slate-300"
        } ${triggerClassName}`}
      >
        <span className={`truncate ${selected ? "text-slate-700" : "text-slate-400"}`}>
          {triggerText}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2"
          className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="mt-1.5 border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              {SearchIcon}
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] bg-white"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto">
            {!list.length && (
              <p className="text-center py-5 text-xs text-slate-400">No matches</p>
            )}
            {list.map((c) => {
              const isSel = selected?.code === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => pick(c)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${
                    isSel ? "bg-[rgb(var(--brand-rgb)/0.05)]" : ""
                  }`}
                >
                  <span className="text-base leading-none flex-shrink-0">{c.flag}</span>
                  <span className="flex-1 min-w-0 text-xs font-semibold text-slate-700 truncate">
                    {c.name}
                  </span>
                  <span className="text-[11px] font-mono text-slate-400 flex-shrink-0">
                    {c.dial}
                  </span>
                  {isSel && (
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={G}
                      strokeWidth="3"
                      className="flex-shrink-0"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── The three linked fields ─────────────────────────────────────────────────
// Reads/writes data.nationality, data.countryOfResidence, data.phone.
export function CountryFields({ data, onChange }) {
  const natCountry = findCountry(data.nationality);
  const resCountry = findCountry(data.countryOfResidence);
  const { country: phoneCountry, rest: phoneRest } = splitPhone(data.phone);

  const joinPhone = (dial, rest) => (rest ? `${dial} ${rest}` : dial);

  // Cascade helpers — fill the OTHER fields only when they are still empty.
  const pickNationality = (c) => {
    const next = { ...data, nationality: c.demonym };
    if (!data.countryOfResidence) next.countryOfResidence = c.name;
    if (!data.phone) next.phone = c.dial;
    onChange(next);
  };

  const pickResidence = (c) => {
    const next = { ...data, countryOfResidence: c.name };
    if (!data.nationality) next.nationality = c.demonym;
    if (!data.phone) next.phone = c.dial;
    onChange(next);
  };

  const pickPhoneCountry = (c) => {
    const next = { ...data, phone: joinPhone(c.dial, phoneRest) };
    if (!data.nationality) next.nationality = c.demonym;
    if (!data.countryOfResidence) next.countryOfResidence = c.name;
    onChange(next);
  };

  const setPhoneNumber = (e) => {
    const num = e.target.value;
    onChange({ ...data, phone: phoneCountry ? joinPhone(phoneCountry.dial, num) : num });
  };

  return (
    <>
      <SearchableCountry
        label="Nationality"
        mode="name"
        placeholder="Select nationality"
        selected={natCountry}
        onSelect={pickNationality}
      />

      <SearchableCountry
        label="Country of Residence"
        mode="name"
        placeholder="Select country"
        selected={resCountry}
        onSelect={pickResidence}
      />

      {/* Phone = dial-code dropdown + free-text number, combined into `phone`. */}
      <div className="col-span-2">
        <FieldLabel>Phone</FieldLabel>
        <div className="flex gap-2">
          <SearchableCountry
            mode="dial"
            placeholder="Code"
            selected={phoneCountry}
            onSelect={pickPhoneCountry}
            className="w-40 flex-shrink-0"
          />
          <input
            type="tel"
            value={phoneRest}
            onChange={setPhoneNumber}
            placeholder="801 234 5678"
            className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.2)] focus:border-[rgb(var(--brand-rgb))] bg-white"
          />
        </div>
      </div>
    </>
  );
}
